"use client";

// SegmentedControl — единый тактильный сегмент-фильтр «Картотека».
// Один визуальный язык для ВСЕХ горизонтальных фильтров/переключателей системы:
// трек: light = well, dark = surface-2 (sh-segment-track) + приподнятая активная клавиша (bg-card + e1).
// Заменяет 5+ ручных копий этого паттерна (study-plan, scenarios, quizzes view-toggle,
// audio-recaps, essays, materials, notifications). Декларативный options-API гарантирует,
// что счётчики, иконки, тач-таргеты и скролл выглядят одинаково везде.
//
// Построен поверх Radix ToggleGroup (type="single") — roving-focus + доступность.
// Деселект (повторный клик по активному → пустое значение) подавлен: фильтр всегда выбран.

import { ToggleGroup as ToggleGroupPrimitive } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "../../lib/cn.js";
import { useEdgeFadeMask } from "../../lib/use-edge-fade-mask.js";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip/index.js";

export interface SegmentedOption<T extends string = string> {
	value: T;
	/** Подпись сегмента. Можно опустить для icon-only (тогда задать ariaLabel). */
	label?: ReactNode;
	/** Иконка слева от подписи (или единственный контент при iconOnly). */
	icon?: ReactNode;
	/** Счётчик справа от подписи. Показывается даже при 0 — если поле задано. */
	count?: number;
	/** Доступное имя — ОБЯЗАТЕЛЬНО для сегментов без текстовой подписи. */
	ariaLabel?: string;
	disabled?: boolean;
}

export interface SegmentedControlProps<T extends string = string> {
	options: ReadonlyArray<SegmentedOption<T>>;
	value: T;
	onValueChange: (value: T) => void;
	/** md (по умолчанию, h-8 клавиша) — основные фильтр-бары. sm (h-7) — плотные ряды. */
	size?: "sm" | "md";
	/** Только иконки без подписей — для view-toggle (сетка/список). */
	iconOnly?: boolean;
	/** Горизонтальный скролл при переполнении (много опций / mobile). */
	scrollable?: boolean;
	/** Растянуть сегменты на всю ширину равными долями. */
	fullWidth?: boolean;
	/** Доступное имя всей группы (например «Фильтр по статусу»). */
	"aria-label"?: string;
	className?: string;
}

const TRACK_BASE =
	"sh-segment-track items-center gap-1 rounded-[var(--radius-md)] border border-border p-1 text-muted-foreground";

const KEY_BASE =
	"inline-flex shrink-0 cursor-pointer select-none items-center justify-center font-medium whitespace-nowrap rounded-[var(--radius-sm)] " +
	"transition-[transform,background-color,box-shadow,color] duration-fast ease-[var(--ease-spring)] " +
	"text-muted-foreground hover:text-foreground active:translate-y-px " +
	"disabled:pointer-events-none disabled:opacity-50";

/**
 * Приподнятая активная клавиша.
 *
 * ⚠️ Считается от `value`, а НЕ через `data-[state=on]:` — и это не вкусовщина.
 * `Toggle` из Radix выставляет `data-state="on"`, но тут же спредит поверх
 * входящие пропсы (`data-state` → `...buttonProps`), а `TooltipTrigger asChild`
 * прокидывает в них СВОЙ `data-state` — «closed», пока подсказка закрыта. У
 * icon-only сегментов (переключатель вида) подсказка обязательна, поэтому
 * атрибут у них навсегда `closed`: селектор не срабатывал, и человек не видел,
 * какой режим включён. Текстовые табы тултипом не оборачиваются — там подсветка
 * работала, отсюда и разное поведение двух соседних контролов в одном тулбаре.
 *
 * `value` — единственный источник правды и для рендера, и для Radix, так что
 * подсветка не может разъехаться с фактическим выбором.
 */
const KEY_ACTIVE = "bg-card text-foreground shadow-e1";

// Высота ТРЕКА (md) фиксируется = высоте поля поиска: h-9 desktop / h-11 mobile
// (ListToolbar WELL_INPUT). Активные клавиши внутри — заметно ниже трека
// (утопленный well + приподнятая клавиша). sm — без явной высоты (трек растёт по
// клавише, плотные ряды). Раньше высоту трека задавала клавиша (h-8 + p-1 + border
// ≈ 40/52px) → трек был выше поиска (фидбек: «переключатель вида и фильтры выше
// строки поиска, и на mobile, и на desktop»).
const TRACK_SIZE = {
	sm: "",
	md: "h-9 pointer-coarse:h-11",
} as const;

const KEY_SIZE = {
	sm: "h-7 px-2.5 text-xs gap-1 pointer-coarse:min-h-10",
	// Клавиша ниже трека (трек h-9=36 → клавиша 24, ~5px инсет сверху/снизу);
	// pointer-coarse: трек h-11=44 → клавиша 32.
	md: "h-6 px-3 text-sm gap-1.5 pointer-coarse:h-8",
} as const;

const ICON_ONLY_SIZE = {
	sm: "size-7 px-0 pointer-coarse:size-10",
	md: "size-6 px-0 pointer-coarse:size-8",
} as const;

export function SegmentedControl<T extends string = string>({
	options,
	value,
	onValueChange,
	size = "md",
	iconOnly = false,
	scrollable = false,
	fullWidth = false,
	className,
	"aria-label": ariaLabel,
}: SegmentedControlProps<T>) {
	// Скролл-режим прячет полосу прокрутки (`scrollbar-hide`), и у уехавших за
	// край сегментов не остаётся НИ ОДНОГО признака: в длинных локалях (kk/hy/uz/az)
	// третья вкладка панели курса не влезает в 328px, человек видит две с
	// половиной и считает, что третьей нет. Маска-градиент у края — тот самый
	// признак; включает её сам режим `scrollable` (нового пропа контрол не
	// заводит) и только по факту переполнения: трек, который влез, не затемняется.
	// Механика общая со строкой контекста репетитора — `useEdgeFadeMask`.
	const edgeFade = useEdgeFadeMask(scrollable);

	const track = (
		<ToggleGroupPrimitive.Root
			type="single"
			value={value}
			// Деселект подавлен — у фильтра всегда есть выбранное значение.
			onValueChange={(next) => {
				if (next) onValueChange(next as T);
			}}
			aria-label={ariaLabel}
			className={cn(
				TRACK_BASE,
				TRACK_SIZE[size],
				fullWidth ? "flex w-full" : "inline-flex",
				// В скролл-режиме трек растёт по контенту, скроллит внешний обёрточный div.
				scrollable ? "w-max" : className,
			)}
		>
			{options.map((opt) => {
				const active = opt.value === value;
				const item = (
					<ToggleGroupPrimitive.Item
						key={opt.value}
						value={opt.value}
						disabled={opt.disabled}
						aria-label={opt.ariaLabel}
						// Возвращаем атрибуту правду там, где его перетёр тултип (см.
						// `KEY_ACTIVE`). Стили на него больше не завязаны, но на
						// `[data-state=on]` смотрят e2e и внешний стайлинг, а клавиша с
						// вечным `closed` врёт им обоим. Пропсы ребёнка в Radix `Slot`
						// перекрывают пропсы триггера, поэтому это доезжает до кнопки.
						data-state={active ? "on" : "off"}
						className={cn(
							KEY_BASE,
							iconOnly ? ICON_ONLY_SIZE[size] : KEY_SIZE[size],
							fullWidth && "flex-1",
							active && KEY_ACTIVE,
						)}
					>
						{opt.icon}
						{!iconOnly && opt.label}
						{!iconOnly && opt.count !== undefined && (
							<span
								className={cn(
									"num tabular-nums text-2xs",
									active ? "text-muted-foreground" : "text-muted-foreground/70",
								)}
							>
								{opt.count}
							</span>
						)}
					</ToggleGroupPrimitive.Item>
				);
				if (iconOnly && opt.ariaLabel) {
					return (
						<Tooltip key={opt.value}>
							<TooltipTrigger asChild>{item}</TooltipTrigger>
							<TooltipContent side="bottom">{opt.ariaLabel}</TooltipContent>
						</Tooltip>
					);
				}
				return item;
			})}
		</ToggleGroupPrimitive.Root>
	);

	if (!scrollable) return track;

	// Внешний скроллер. Вертикальный зазор -my-2/py-2 — чтобы тень-ребро (shadow-e1,
	// ~8px вниз) активной клавиши не обрезалась горизонтальным overflow (overflow-x:auto
	// форсит overflow-y:auto по CSS-спеке). Негативный margin держит layout-высоту.
	// Маска горизонтальная — по вертикали трек полностью непрозрачен, тень цела.
	return (
		<div
			ref={edgeFade.ref}
			className={cn(
				"-my-2 max-w-full overflow-x-auto overscroll-x-contain scrollbar-hide py-2",
				className,
			)}
			style={edgeFade.style}
		>
			{track}
		</div>
	);
}
